import React from 'react';
import PropTypes from 'prop-types';

import http from 'api/http';
import { boatAPI } from 'api/requests';

import Row from './Row';
import Pagination from 'Components/Pagination';
import RowsSelector from 'Components/RowsSelector';
import FiltersSection from './FiltersSection';
import TableHeader from './TableHeader';
import EmptyList from 'Components/EmptyList';
import Store from '../../../../registerIDBWorker';
import { Input, inputStyleClasses } from 'Components/Input'

const SEARCHABLE_FIELDS = ['id', 'name'];

export default class List extends React.Component {
  constructor(props) {
    super(props);

    this.defaultState = {
      expandedRowId: 0,
      activePage: 1,
      selectedRowsCount: 10,
      sortedBy: 'id',
      sortAsc: -1,
      filter: {
        year: ''
      },
    };

    const storedState = props.user.getStoredState('boatListState', this.defaultState);
    props.user.updateURLSearchString(storedState);

    this.state = {
      boatList: [],
      listRequestInProcess: true,
      isModalOpen: false,
      ...this.defaultState,
      ...storedState
    };
  }

  componentDidMount() {
    const {user} = this.props;

    this.handleSelectFilter('year')(user.getSelectedYear() || '');

    this._loadFromStore();

    http.get(boatAPI.list)
      .then(({data}) => {
        Store.insert(`${user.getActiveRole()}_lists`, data, user.getManufacturerCode())
          .then(() => {
            this._loadFromStore(() => {
              this.setState({listRequestInProcess: false});
            });
          });
      });
  }

  componentDidUpdate() {
    if ((!this.state.filter || !this.state.filter.year) && this.props.currentModelYear) {
      this.handleSelectFilter('year')(this.props.currentModelYear);
    }
  }

  componentWillUnmount() {
    this.props.user.saveState('boatListState', this.state, ['boatList', 'listRequestInProcess', 'isModalOpen', 'year']);
  }

  _loadFromStore = (cb = () => {}) => {
    const {user} = this.props;

    Store.select(`${user.getActiveRole()}_lists`, user.getManufacturerCode())
      .then(boatList => {
        this.setState({boatList}, cb);
      });
  };

  handleRowsSelect = (rowsCountSelected, totalRowsCount) => {
    const {user} = this.props;

    const selectedRowsCount = (rowsCountSelected && rowsCountSelected < totalRowsCount) ? rowsCountSelected : 0;

    const state = {selectedRowsCount, activePage: 1};
    this.setState(state, user.updateURLSearchString(state));
  };

  _handleActivePageChange = activePage => {
    const {user} = this.props;
    this.setState({activePage}, user.updateURLSearchString({activePage}));
  };

  _filterList = boatList => {
    const {filter, searchString} = this.state;

    const doesSearchableFieldsContainSearchString = (element) => {
      for (let searchableFieldIndex in SEARCHABLE_FIELDS) {
        if (element[SEARCHABLE_FIELDS[searchableFieldIndex]]) {
          if (element[SEARCHABLE_FIELDS[searchableFieldIndex]].toString().toLowerCase()
            .includes(searchString.toLowerCase())) return true;
        }
      }
      return false;
    };

    return boatList.filter(element => {
      for (let filterFieldName in filter) {
        if (element[filterFieldName].toString().toLowerCase() !== filter[filterFieldName].toString().toLowerCase()) {
          return false;
        }
      }
      return (!searchString || doesSearchableFieldsContainSearchString(element));
    });
  };

  handleSelectFilter = filterFieldName => filterElement => {
    const {filter} = this.state;
    const {user} = this.props;
    const newFilterFieldValue = filterElement.value !== undefined ? filterElement.value : filterElement;

    if (newFilterFieldValue === 'All') delete filter[filterFieldName];
    else filter[filterFieldName] = newFilterFieldValue;

    if (filterFieldName === 'year') {
      if (filterElement === 'All') {
        user.saveSelectedYear('');
      } else {
        user.saveSelectedYear(filterElement);
      }
    }

    this.setState({filter, activePage: 1},
      () => {
        user.updateURLSearchString({filter, activePage: 1});
      }
    );
  };

  handleSearchStringInput = value => {
    const {user} = this.props;
    this.setState({searchString:value, activePage:1}, () => {user.updateURLSearchString({searchString:value});});
  };

  _sortList = (a, b) => {
    const {sortedBy, sortAsc} = this.state;

    switch (sortedBy) {
      case 'id':
        return +a.id > +b.id ? sortAsc : -sortAsc;
      default:
        return (a[sortedBy] || ' ').toString().toLowerCase().replace(/\s+/g, ' ') > (b[sortedBy] || ' ').toString().toLowerCase().replace(/\s+/g, ' ') ? sortAsc : -sortAsc;
    }
  };

  handleSort = sortedBy => () => {
    this.setState(prevState => ({
      sortedBy,
      sortAsc: -prevState.sortAsc
    }), this.props.user.updateURLSearchString({sortedBy, sortAsc: this.state.sortAsc}));
  };

  _getList = () => {
    const {boatList, expandedRowId, activePage, selectedRowsCount, sortedBy, listRequestInProcess} = this.state;
    const {user, currentModelYear, createActionsList, selectAction} = this.props;
    const filteredList = this._filterList(boatList).sort(this._sortList);
    const sliceFrom = (activePage - 1) * selectedRowsCount;
    const sliceTo = selectedRowsCount ? activePage * selectedRowsCount : filteredList.length;

    let resultList = [];
    let resultLength = 0;

    if (!boatList.length) {
      if (listRequestInProcess) {
        resultList = [
          <div key="spinner" className='ect-spinner _height_120'>
            <div className='ect-spinner__image' style={{width: 50, height: 50}} />
          </div>
        ];
      } else {
        resultList = [
          <EmptyList key="empty">
            No boats for selected manufacturer.
          </EmptyList>
        ];
      }
    } else if (!filteredList.length) {
      resultList = [
        <EmptyList key="empty">
          No boats found with selected filters.
        </EmptyList>
      ];
    } else {
      resultList = filteredList.slice(sliceFrom, sliceTo).map((boat, index) => {
        return (
          <Row
            key={boat.id}
            boat={boat}
            isOdd={!!(index % 2)}
            toggleRow={this.toggleRow}
            expandedRowId={expandedRowId}
            currentModelYear={currentModelYear}
            createActionsList={createActionsList}
            selectAction={selectAction}
            user={user}
            updateListEntry={this.updateListEntry}
            deleteListEntry={this.deleteListEntry}
            sortedBy={sortedBy}
          />
        );
      });
      resultLength = filteredList.length;
    }

    return {resultList, resultLength};
  };

  toggleRow = expandedRowId => () => {
    if (expandedRowId === this.state.expandedRowId) expandedRowId = '';

    this.setState({expandedRowId});
  };

  updateListEntry = (oldEntry, newEntry) => {
    const {user} = this.props;
    let {boatList} = this.state;

    let dataSource = newEntry.status ? newEntry : {...oldEntry, status:newEntry};

    if (typeof newEntry === 'string') {
      oldEntry.status = newEntry;
    } else if (newEntry.year === oldEntry.year) {
      const oldEntryIndex = boatList.indexOf(oldEntry);
      boatList.splice(oldEntryIndex, 1, newEntry);
    } else {
      boatList.push(newEntry);
    }

    Store.insert(`${user.getActiveRole()}_lists`, dataSource, user.getManufacturerCode());
    this.setState({boatList});
  };

  deleteListEntry = oldEntry => {
    const {user} = this.props;
    let {boatList} = this.state;

    const oldEntryIndex = boatList.indexOf(oldEntry);
    boatList.splice(oldEntryIndex, 1);
    Store.delete(`${user.getActiveRole()}_lists`, oldEntry);
    this.setState({boatList});
  };

  render() {
    const {activePage, selectedRowsCount, sortAsc, sortedBy, filter, searchString} = this.state;
    const {currentModelYear} = this.props;
    const {resultList, resultLength} = this._getList();

    return (
      <section className='scene__scene-wrapper'>
        <section className='scene__content-wrapper list-scene__content-wrapper'>
          <header className='scene__scene-header list-scene__scene-header'>
            Master Model List
            <Input
              mainWrapperClassName='list-scene__header-search-field-wrapper'
              styleClass={inputStyleClasses.searchBar}
              placeholder='Type here to search'
              maxLength={250}
              value={searchString}
              onChange={this.handleSearchStringInput}
            />
          </header>
          <FiltersSection
            handleRowsSelect={this.handleRowsSelect}
            handleSelectFilter={this.handleSelectFilter}
            filterState={filter}
            currentModelYear={currentModelYear}
            allRowsCount={resultLength}
            selectedRowsCount={selectedRowsCount}
          />

          <TableHeader handleSortClick={this.handleSort} order={sortAsc === 1} sortedBy={sortedBy} />

          {resultList}

          <footer className='list-scene__footer'>
            <RowsSelector
              allRowsCount={resultLength}
              selectedRowsCount={selectedRowsCount}
              handleRowsSelect={this.handleRowsSelect}
            />
            <Pagination
              className='_height_80'
              activePage={activePage}
              handleClick={this._handleActivePageChange}
              allRowsCount={resultLength}
              selectedRowsCount={selectedRowsCount}
            />
          </footer>
        </section>
      </section>
    );
  }
}

List.propTypes = {
  list: PropTypes.array.isRequired
};



// WEBPACK FOOTER //
// ./src/app/Scenes/BoatManufacturer/List/index.jsx